import React from 'react';

import {
  Box,
  Grid,
  Select,
  SelectChangeEvent,
  MenuItem,
  Typography,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';

import { CompanyTypeLabelEnum, getAddressText } from '@utils/constants';
import { useAppSelector } from '@store/hooks';
import {
  selectActiveBankAccount,
  selectBankAccounts,
  selectActiveCompany,
  selectLatestProductsStatusInfo,
} from '@features/workspace/store/selectors';
import { LatestProductStart } from '@features/workspace/components/shared/latest-product-start';
import { LatestProductStatus } from '@features/workspace/components/shared/latest-product-status';
import history from '@app/services/history';
import { LayoutType } from '../const';

const useStyles = makeStyles()({
  header: {
    backgroundColor: '#1F4561',
    color: '#FFFFFF',
    borderRadius: '30px 30px 0px 0px',
  },
  bankAccountSelectPaper: {
    border: '3px solid red',
  },
});

interface IProps {
  layoutType?: LayoutType;
}

const PageHeader: React.FC<IProps> = ({ layoutType = LayoutType.CUSTOMER }) => {
  const { classes } = useStyles();

  const company = useAppSelector(selectActiveCompany);
  const latestProductsInfo = useAppSelector(selectLatestProductsStatusInfo);
  const bankAccounts = useAppSelector(selectBankAccounts);
  const activeBankAccount = useAppSelector(selectActiveBankAccount);

  const onActiveBankAccountChange = (accNumber: string) => {
    history.push(`/customer/banking/${accNumber}/transactions`);
  };

  return (
    <>
      <Box className={classes.header} display="flex" px={5} py={2}>
        <Box display="flex" flexGrow={1}>
          <Grid container spacing={2}>
            {/* Company name, type and address */}
            <Grid item xs={6}>
              <Box
                display="flex"
                flexDirection="column"
                flexGrow={1}
                pb={4}
                pt={3}
              >
                <Box alignItems="center" display="flex">
                  <Box
                    display="flex"
                    style={{
                      fontWeight: 700,
                      fontSize: '18px',
                    }}
                  >
                    {company?.name}
                  </Box>
                  <Box
                    display="flex"
                    ml={1}
                    style={{
                      fontWeight: 400,
                      fontSize: '14px',
                    }}
                  >
                    {company && CompanyTypeLabelEnum[company?.companyType]}
                  </Box>
                </Box>
                <Box
                  display="flex"
                  mt={0.5}
                  style={{
                    fontWeight: 400,
                    fontSize: '12px',
                  }}
                >
                  {getAddressText(company?.address)}
                </Box>
              </Box>
            </Grid>
            {/* Customer and documents */}
            {layoutType !== LayoutType.BANKING && (
              <>
                {/* 2nd col info - applications */}
                <Grid item xs={2}>
                  <Box alignItems="center" display="flex" height="100%">
                    <Box display="flex" flexDirection="column">
                      {latestProductsInfo.map((productInfo: any) => (
                        <Box
                          key={`header-product-info-${productInfo?.latestProduct?.item?.id}`}
                          display="flex"
                        >
                          <LatestProductStart productInfo={productInfo} />
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Grid>
                {/* 3rd col info - banking submission */}
                {/* TODO: https://www.figma.com/file/iRx0JDsL5noAY1d25KzSco/Lula-Back-office?node-id=1692%3A24681&mode=dev */}
                <Grid item xs={2} />
                {/* 4th col info - status */}
                <Grid item xs={2}>
                  <Box display="flex" justifyContent="flex-end" pt={3}>
                    <LatestProductStatus productInfo={latestProductsInfo[0]} />
                  </Box>
                </Grid>
              </>
            )}
            {/* Banking */}
            {layoutType === LayoutType.BANKING && (
              <>
                {/* 2nd col info - bank account selector */}
                <Grid item xs={3}>
                  <Box display="flex" flexDirection="column" pt={2}>
                    <Box display="flex">
                      <Select
                        MenuProps={{
                          sx: {
                            '& .MuiMenu-paper': {
                              borderRadius: '8px',
                            },
                          },
                        }}
                        sx={{
                          width: 243,
                          backgroundColor: '#fff',
                          borderRadius: '8px',
                          '& .MuiInputBase-input': {
                            fontSize: '22px',
                            fontWeight: 700,
                            padding: '0 14px',
                            lineHeight: '35px',
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderRadius: '8px',
                          },
                          '&.Mui-focused': {
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#0000003b',
                            },
                          },
                        }}
                        value={activeBankAccount?.accountNumber}
                        onChange={(event: SelectChangeEvent<unknown>) => {
                          onActiveBankAccountChange(
                            event.target.value as string
                          );
                        }}
                      >
                        {bankAccounts.map((acc) => (
                          <MenuItem
                            key={`bank-account-selector-${acc.accountNumber}`}
                            value={acc.accountNumber}
                          >
                            {acc.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </Box>
                    <Box display="flex" px={2} py={1.5}>
                      <Typography variant="h6">
                        Acc. no: {activeBankAccount?.accountNumber}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                {/* 3rd col info - bank account balance */}
                <Grid item xs={3}>
                  {activeBankAccount && (
                    <Box display="flex" justifyContent="flex-end">
                      <Box
                        display="flex"
                        flexDirection="column"
                        minWidth={150}
                        pt={4}
                      >
                        <Box display="flex">
                          <Typography variant="caption">Balance</Typography>
                        </Box>
                        <Box display="flex">
                          {activeBankAccount.currency !== 'ZAR' && (
                            <Typography variant="subtitle1">
                              {activeBankAccount.amount}{' '}
                              {activeBankAccount.currency}
                            </Typography>
                          )}
                          {activeBankAccount.currency === 'ZAR' && (
                            <Typography variant="subtitle1">
                              R {activeBankAccount.amount}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Grid>
              </>
            )}
          </Grid>
        </Box>
      </Box>
    </>
  );
};

export default PageHeader;
